import type { TFunction } from 'i18next';

import { cabecalhoDaConta, nomeDoArquivo } from '@/domain/configuracao/helpers/exportIdentidade';

import { IFatiaDaProducao } from './ocupacaoPorMarca';

/** O nome do arquivo: `CosturAI_DRO_visao-executiva_2026-10-19.xlsx`. */
const FILE_PREFIX = 'visao-executiva';

/** Uma coluna da aba: o cabeçalho, a largura e o que ela lê de cada marca. */
interface IColunaDaProducao {
  header: string;
  width: number;
  value: (fatia: IFatiaDaProducao) => string | number;
}

function colunas(t: TFunction): Array<IColunaDaProducao> {
  return [
    {
      header: t('visaoExecutiva.exportar.colMarca'),
      width: 28,
      value: (fatia) => (fatia.marca === '' ? t('visaoExecutiva.producao.semMarca') : fatia.marca),
    },
    // As TRÊS medidas vão juntas na mesma linha: quem abre a planilha não escolhe uma
    // chave como na tela, e um arquivo por medida seria três arquivos para conferir.
    { header: t('visaoExecutiva.exportar.colHoras'), width: 14, value: (fatia) => fatia.horas },
    { header: t('visaoExecutiva.exportar.colPecas'), width: 14, value: (fatia) => Math.round(fatia.pecas) },
    {
      header: t('visaoExecutiva.exportar.colFaturamento'),
      width: 18,
      value: (fatia) => fatia.faturamento,
    },
    { header: t('visaoExecutiva.exportar.colOps'), width: 12, value: (fatia) => fatia.ops },
  ];
}

interface IExportVisaoParams {
  /** TODAS as marcas com carga, não só as cinco da pizza: a planilha não tem "Outras". */
  fatias: Array<IFatiaDaProducao>;
  /** O período escolhido, escrito: a planilha sai da tela e precisa dizer de quando é. */
  periodoTexto: string;
  t: TFunction;
  today: Date;
  /** The active account: it names the file and opens the first row. */
  conta: string;
}

/**
 * A Visão executiva em `.xlsx`, com a aba "Quem ocupa a produção".
 *
 * A planilha leva as TRÊS medidas de cada marca e o período no cabeçalho, porque fora da
 * tela não existe a chave que alterna a medida nem o seletor que diz a janela. Números
 * vão como número, para a planilha somar e ordenar sem ninguém limpar nada antes.
 *
 * O escritor é carregado sob demanda, como na Carteira: ninguém baixa o gerador de xlsx
 * só para abrir a tela.
 */
export async function exportVisaoExecutivaToXlsx({
  fatias,
  periodoTexto,
  t,
  today,
  conta,
}: IExportVisaoParams): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const cols = colunas(t);

  const tituloRow = cabecalhoDaConta(conta, t, today, cols.length);
  const periodoRow = [
    { value: t('visaoExecutiva.exportar.periodo', { periodo: periodoTexto }), fontWeight: 'bold' as const },
    ...Array.from({ length: cols.length - 1 }, () => null),
  ];
  const headerRow = cols.map((coluna) => ({ value: coluna.header, fontWeight: 'bold' as const }));
  const bodyRows = fatias.map((fatia) =>
    cols.map((coluna) => {
      const valor = coluna.value(fatia);
      return typeof valor === 'number' ? { type: Number, value: valor } : { type: String, value: valor };
    }),
  );

  await writeXlsxFile([tituloRow, periodoRow, headerRow, ...bodyRows], {
    sheet: t('visaoExecutiva.exportar.aba'),
    columns: cols.map((coluna) => ({ width: coluna.width })),
  }).toFile(nomeDoArquivo(conta, FILE_PREFIX, today));
}
